import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { ADNode } from '@atlaskit/editor-common/utils/validator';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ALLOWED_FLAG_MESSAGES } from '@confluence/editor-flags/entry-points/allowedFlagConstants.ts';

import { FLAG_ERROR_MESSAGES } from './constants.ts';

type Event = {
	flags?: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	actionSubject: string;
	attributes?: { error?: string };
	source: string;
	customFlagMessage?: { title: any; description: any } | null;
	isSuccess: boolean;
	actionSubjectId?: string;
};

type AnalyticsEvent = Event & { isSuccess: boolean };

export const i18n: {
	defaultErrorBlogifyFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	defaultErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	alternativeErrorBlogifyFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pagePermissionsErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spacePermissionsErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageDeletedErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	spaceRestrictionsErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	archivedPageErrorBlogifyFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	defaultErrorBlogifyFlagTitle: {
		id: 'publish-page-as-blog.flags.error-message-title',
		defaultMessage: 'We had an issue publishing the blog',
		description: 'Flag title shown after publish of page to a blog failed',
	},
	defaultErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.error-message-description',
		defaultMessage: 'Refresh the page and try again.',
		description: 'Message on error flag after failure to publish page to blog',
	},
	alternativeErrorBlogifyFlagTitle: {
		id: 'publish-page-as-blog.flags.alternative-error-message-title',
		defaultMessage: 'We can’t convert this page',
		description:
			'Flag title shown for more specific errors when a page cannot be converted to a blog',
	},
	pagePermissionsErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.page-permissions-error-message-description',
		defaultMessage:
			'Restrictions have been applied to this item recently that prevent its conversion.',
		description: 'Flag content shown when a page cannot be converted to a blog due to restrictions',
	},
	spacePermissionsErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.space-permissions-error-message-description',
		defaultMessage: 'You need additional permissions in the destination space to convert it.',
		description: 'Flag content shown when a page cannot be converted to a blog due to restrictions',
	},
	pageDeletedErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.page-deleted-error-message-description',
		defaultMessage: 'While you were working with this content, it was deleted by someone else.',
		description: 'Flag content shown when a page cannot be converted to a blog due to restrictions',
	},
	spaceRestrictionsErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.space-restrictions-error-message-description',
		defaultMessage: 'Restrictions in the destination space prevent this item from being converted.',
		description: 'Flag content shown when a page cannot be converted to a blog due to restrictions',
	},
	archivedPageErrorBlogifyFlagDescription: {
		id: 'publish-page-as-blog.flags.archived-page-error-message-description',
		defaultMessage: 'While you were working with this page, it was archived by someone else.',
		description: 'Flag content shown when a page cannot be converted to a blog due to restrictions',
	},
});

const fireAnalyticsEvent = ({
	createAnalyticsEvent,
	isSuccess,
	actionSubject,
	attributes,
	source,
	actionSubjectId,
}: AnalyticsEvent) => {
	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			actionSubject,
			action: isSuccess ? 'success' : 'failed',
			source,
			...(attributes && { attributes }),
			...(actionSubjectId && { actionSubjectId }),
		},
	}).fire();
};

export const publishFromPageToBlogEvent = ({
	createAnalyticsEvent,
	actionSubject,
	attributes,
	source,
	isSuccess,
	actionSubjectId,
}: Event): void => {
	fireAnalyticsEvent({
		createAnalyticsEvent,
		isSuccess,
		actionSubject,
		attributes,
		source,
		actionSubjectId,
	});
};

export const publishPageAsBlogFailureEventAndFlag = ({
	flags,
	createAnalyticsEvent,
	actionSubject,
	actionSubjectId,
	attributes,
	source,
	customFlagMessage,
}: Omit<Event, 'isSuccess'>): void => {
	fireAnalyticsEvent({
		createAnalyticsEvent,
		isSuccess: false,
		actionSubject,
		attributes,
		source,
		actionSubjectId,
	});
	flags &&
		void flags.showErrorFlag({
			title:
				customFlagMessage && customFlagMessage.title ? (
					customFlagMessage.title
				) : (
					<FormattedMessage {...blogifyErrorMessage(attributes?.error).title} />
				),
			description:
				customFlagMessage && customFlagMessage.description ? (
					customFlagMessage.description
				) : (
					<FormattedMessage {...blogifyErrorMessage(attributes?.error).description} />
				),
		});
};

/***
 *
 * @returns an error string if the error is one that the editor can throw during a publish event
 ***/
export const knownEditorError = (errorMessage: string): string => {
	let knownEditorErrorMessage = '';
	errorMessage &&
		Object.values(ALLOWED_FLAG_MESSAGES).some(function (value) {
			if (errorMessage.includes(value)) {
				knownEditorErrorMessage = value;
				return true;
			}
		});

	return knownEditorErrorMessage;
};

/**
 * throws an error if for some reason we cannot get the editor content
 * for logging purposes
 * @returns void
 **/
export const handleEditorPublishPageAsBlogError = (adf: ADNode | null): void => {
	if (!adf) {
		throw new Error('Could not get the Editor Content');
	}
};

export const blogifyErrorMessage = (
	errorMessage?: string,
): {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	description: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} => {
	if (errorMessage?.includes(FLAG_ERROR_MESSAGES.PAGE_PERMISSIONS)) {
		return {
			title: i18n.alternativeErrorBlogifyFlagTitle,
			description: i18n.pagePermissionsErrorBlogifyFlagDescription,
		};
	}
	if (errorMessage?.includes(FLAG_ERROR_MESSAGES.SPACE_PERMSSIONS)) {
		return {
			title: i18n.alternativeErrorBlogifyFlagTitle,
			description: i18n.spacePermissionsErrorBlogifyFlagDescription,
		};
	}
	if (
		errorMessage?.includes(FLAG_ERROR_MESSAGES.DELETED_PAGE) ||
		errorMessage?.includes(FLAG_ERROR_MESSAGES.DELETED_PAGE_ALT)
	) {
		return {
			title: i18n.alternativeErrorBlogifyFlagTitle,
			description: i18n.pageDeletedErrorBlogifyFlagDescription,
		};
	}
	if (errorMessage?.includes(FLAG_ERROR_MESSAGES.SPACE_NOT_FOUND)) {
		return {
			title: i18n.alternativeErrorBlogifyFlagTitle,
			description: i18n.spaceRestrictionsErrorBlogifyFlagDescription,
		};
	}
	if (errorMessage?.includes(FLAG_ERROR_MESSAGES.ARCHIVED_PAGE)) {
		return {
			title: i18n.alternativeErrorBlogifyFlagTitle,
			description: i18n.archivedPageErrorBlogifyFlagDescription,
		};
	}
	return {
		title: i18n.defaultErrorBlogifyFlagTitle,
		description: i18n.defaultErrorBlogifyFlagDescription,
	};
};
